import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Target } from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

/**
 * Content gap analysis — benchmark draft outline against top-10 SERP results.
 * Calls `onSeedDraft` when the user chooses to push selected H2s into the editor.
 */
export default function GapTab({ projectId, onSeedDraft }) {
  const [gKeyword, setGKeyword] = useState("");
  const [gContent, setGContent] = useState("");
  const [gap, setGap] = useState(null);
  const [gapBusy, setGapBusy] = useState(false);
  const [selectedH2, setSelectedH2] = useState({});

  const toggleH2 = (heading) => setSelectedH2((s) => ({ ...s, [heading]: !s[heading] }));

  const runGap = async () => {
    if (!gKeyword.trim()) { toast.error("Add a target keyword"); return; }
    setGapBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/content/gap`, {
        target_keyword: gKeyword, content: gContent,
      });
      setGap(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setGapBusy(false);
  };

  const insertSelectedToDraft = () => {
    const picks = Object.keys(selectedH2).filter((h) => selectedH2[h]);
    if (picks.length === 0) { toast.error("Select at least one heading"); return; }
    const skeleton = `# ${gKeyword}\n\n${picks.map((h) => `## ${h}\n\n[autogen]\n`).join("\n")}`;
    onSeedDraft({ title: gKeyword, target_keyword: gKeyword, content: skeleton });
    toast.success(`Inserted ${picks.length} H2 section(s) into a new draft`);
  };

  return (
    <div className="mt-4 space-y-4">
      <p className="text-sm text-muted-foreground">Benchmark your draft against the top-10 ranking pages for a target keyword. <span className="font-mono-data text-[11px] uppercase tracking-widest text-amber-700">data · mocked</span></p>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 border border-border bg-white p-4 space-y-3">
          <Label className="font-mono-data text-xs uppercase tracking-widest">Target keyword</Label>
          <Input value={gKeyword} onChange={(e)=>setGKeyword(e.target.value)} placeholder="e.g. 'content marketing'"
            className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground" data-testid="gap-keyword" />
          <Label className="font-mono-data text-xs uppercase tracking-widest">Your draft (optional)</Label>
          <Textarea value={gContent} onChange={(e)=>setGContent(e.target.value)} placeholder="# Paste your draft markdown so we can flag missing H2s and terms..."
            className="rounded-none border-foreground/30 focus-visible:ring-foreground font-mono-data text-xs min-h-[260px]" data-testid="gap-content" />
          <Button onClick={runGap} disabled={gapBusy} className="w-full rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="gap-run">
            <Target className="w-4 h-4 mr-1.5" /> {gapBusy ? "Analyzing…" : "Run gap analysis"}
          </Button>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!gap ? (
            <div className="border border-dashed border-foreground/30 p-10 text-center">
              <div className="w-11 h-11 mx-auto bg-foreground grid place-items-center text-background mb-3">
                <OtterIcon size={26} />
              </div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Drop in a keyword and Arlo will dive into the top-10 results — common themes, missing H2s, and the terms your draft skipped.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border">
                {[
                  ["Avg words", gap.competitor_avg_word_count],
                  ["Min", gap.competitor_min_word_count],
                  ["Max", gap.competitor_max_word_count],
                  ["Your draft", gap.your_word_count],
                ].map(([l,v])=>(
                  <div key={l} className="bg-white p-3">
                    <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                    <div className="font-semibold text-xl mt-0.5">{v.toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h4 className="font-display tracking-tight text-base">Missing H2 sections ({gap.missing_h2.length})</h4>
                    <p className="text-xs text-muted-foreground mt-1">Select the ones you want, then insert them into a new draft skeleton.</p>
                  </div>
                  <Button size="sm" onClick={insertSelectedToDraft} className="rounded-none h-9 bg-foreground hover:bg-foreground/90" data-testid="gap-insert-button">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Insert selected into draft
                  </Button>
                </div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {gap.missing_h2.length === 0 && <li className="text-muted-foreground">None — your outline matches the SERP well.</li>}
                  {gap.missing_h2.map((h,i)=>(
                    <li key={i} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!selectedH2[h.heading]}
                        onChange={()=>toggleH2(h.heading)}
                        className="w-4 h-4 accent-foreground"
                        data-testid={`gap-h2-checkbox-${i}`}
                      />
                      <span className="flex-1">{h.heading}</span>
                      <span className="font-mono-data text-xs text-muted-foreground shrink-0">{h.competitor_count}× in top 10</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-border bg-white p-4">
                <h4 className="font-display tracking-tight text-base">Missing semantic terms</h4>
                <p className="text-xs text-muted-foreground mt-1">Terms competitors emphasize in headings that your draft doesn&apos;t mention.</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {gap.missing_terms.length === 0 && <span className="text-sm text-muted-foreground">None.</span>}
                  {gap.missing_terms.map((t,i)=>(
                    <Badge key={i} variant="outline" className="rounded-none font-mono-data text-[11px] uppercase">
                      {t.term} <span className="ml-1 text-muted-foreground">·{t.competitor_count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="border border-border bg-white overflow-x-auto">
                <div className="px-4 py-3 border-b border-border">
                  <h4 className="font-display tracking-tight text-base">Top-10 SERP outlines</h4>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/40 font-mono-data text-[10px] uppercase tracking-widest">
                      <th className="text-left px-4 py-2">#</th>
                      <th className="text-left px-4 py-2">Domain</th>
                      <th className="text-left px-4 py-2">Title</th>
                      <th className="text-right px-4 py-2">Words</th>
                      <th className="text-left px-4 py-2">H2s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gap.serp.map((r) => (
                      <tr key={r.rank} className="border-b border-border last:border-0">
                        <td className="px-4 py-2 font-mono-data">{r.rank}</td>
                        <td className="px-4 py-2 font-medium">{r.domain}</td>
                        <td className="px-4 py-2 text-xs">{r.title}</td>
                        <td className="px-4 py-2 text-right font-mono-data">{r.word_count.toLocaleString()}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{r.h2.slice(0,3).join(" · ")}{r.h2.length>3 && " · …"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
